import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { FormControl, FormGroup, FormLabel, MenuItem, Stack, Typography } from '@mui/material';
import ActionButton from 'common/components/ActionButton';
import CustomTextFieldDescription from 'common/components/CustomTextFieldDescription';
import CustomTextValidator from 'common/components/CustomTextValidators';
import RoundedSelect from 'common/components/RoundedSelect';
import RenderFormFields from 'common/features/renderForms/RenderFormFields';
import { getDynamicFields, getNestedData } from 'common/features/renderForms/utils';
import useFlexBoxStyles from 'common/flexBox';
import LayoutForm from 'common/layouts/LayoutForm';
import { useFormFields } from 'libs/hooksLib';
import { isEqual } from 'lodash';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useDispatch, useSelector } from 'react-redux';
import {
    cancelCloseCustomDialogAction,
    closeCustomDialogAction,
    getIsDialogOnClose,
} from 'redux/slices/customDialogSlice';
import { getFormConfig } from 'redux/slices/formSlice';
import { fetchGroupsForSelectAction, getGroupInheritTimezoneAction } from 'redux/slices/groupSlice';
import { getInvitationsSavePending } from 'redux/slices/invitationSlice';
import { getGroupFormDirty, setGroupFormDirty } from 'redux/slices/profileSlice';
import SelectGroups from '../../iCalendar/filters/SelectGroups';

const GroupForm = (props) => {
    const { group, onSubmit, action, isProfile } = props;
    const { t, i18n } = useTranslation();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const isDrawerOnClose = useSelector(getIsDialogOnClose);
    const savePending = useSelector(getInvitationsSavePending);
    const cancelConfirmTitle = t('user.userInviteForm.cancelConfirm');
    const cancelConfirmMessage = t('channel.form.cancelMessage');
    const formConfig = useSelector(getFormConfig);
    moment.locale(i18n.language || 'en');
    const [inheritTimezone, setInheritTimezone] = useState('');
    const groupFormDirty = useSelector(getGroupFormDirty);
    const initialForm = useMemo(() => {
        const initial = {
            name: group.name,
            description: group.description,
            externalId: group.externalId,
            parentId: group.parentId,
            timezone: group.timezone || '',
        };
        const dynamic = getDynamicFields(formConfig, group.extraFields);
        return { ...initial, ...dynamic };
    }, [group, formConfig]);

    const [fields, handleFieldChange, resetForm] = useFormFields(initialForm);

    const userDidChange = useCallback(() => {
        return !isEqual(initialForm, fields);
    }, [fields, initialForm]);

    useEffect(() => {
        if (isProfile) {
            if (userDidChange()) {
                dispatch(setGroupFormDirty(true));
            } else {
                dispatch(setGroupFormDirty(false));
            }
        }
    }, [dispatch, isProfile, userDidChange]);

    useEffect(() => {
        if (!fields.timezone && group.id) {
            dispatch(
                getGroupInheritTimezoneAction({
                    id: group.id,
                    queryParams: { inheritedTimezoneOnly: true },
                    onSuccess: (result) => {
                        if (result?.timezone) {
                            setInheritTimezone(result?.timezone);
                        } else {
                            setInheritTimezone('');
                        }
                    },
                }),
            );
        } else {
            setInheritTimezone('');
        }
    }, [fields.timezone, dispatch, group]);

    const finalCancel = useCallback(() => {
        dispatch(closeCustomDialogAction());
    }, [dispatch]);

    const manageCancel = useCallback(() => {
        if (!userDidChange()) {
            finalCancel();
        }
        setOpenCancelDlg(true);
    }, [finalCancel, userDidChange]);

    useEffect(() => {
        if (isDrawerOnClose) {
            manageCancel();
        }
    }, [isDrawerOnClose, manageCancel]);

    const [openCancelDlg, setOpenCancelDlg] = useState(false);

    const cancelDlg = () => {
        setOpenCancelDlg(false);
        dispatch(cancelCloseCustomDialogAction());
    };

    const cancel = (event) => {
        event.preventDefault();
        manageCancel();
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const data = {
            name: fields.name?.trim() || '',
            description: fields.description?.trim() || '',
            externalId: fields.externalId?.trim() || '',
            parentId: fields.parentId?.trim() || null,
            timezone: fields.timezone === '' ? null : fields.timezone,
            extraFields: {
                ...(group?.extraFields || {}),
                ...getNestedData(formConfig, fields),
            },
        };
        onSubmit({
            data,
            onSuccess: (response) => {
                enqueueSnackbar(group?.id ? t('group.groupForm.editedDone') : t('group.groupForm.createdDone'), {
                    variant: 'success',
                });
                finalCancel();
                resetForm({
                    name: response.name,
                    description: response.description,
                    externalId: response.externalId,
                    parentId: response.parentId,
                    timezone: response.timezone ?? '',
                    ...getDynamicFields(formConfig, response.extraFields),
                });
            },
            onFailure: (error) => {
                enqueueSnackbar((typeof error === 'string' && error) || t('app.common.error'), {
                    variant: 'error',
                });
            },
        });
    };

    function handleError() {
        enqueueSnackbar(t('user.userInviteForm.formError'), {
            variant: 'error',
        });
    }

    return (
        <LayoutForm
            title={action}
            confirmDlg={{
                open: openCancelDlg,
                accept: finalCancel,
                cancel: cancelDlg,
                titleDlg: cancelConfirmTitle,
                messageDlg: cancelConfirmMessage,
            }}
        >
            <ValidatorForm
                onSubmit={handleSubmit}
                onError={handleError}
                className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden)}
            >
                <Stack direction="column" spacing={2} className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone)}>
                    <FormGroup>
                        <FormLabel>{t('contact.name')}</FormLabel>
                        <CustomTextValidator
                            name="name"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            value={fields.name}
                            onChange={handleFieldChange}
                            validators={['required']}
                            errorMessages={[t('user.userInviteForm.emailRequired')]}
                        />
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>{t('user.groupForm.parent')}</FormLabel>
                        <SelectGroups
                            fetchAction={fetchGroupsForSelectAction}
                            onSubmit={(e) => {
                                handleFieldChange({
                                    target: {
                                        name: 'parentId',
                                        value: e.target.value,
                                    },
                                });
                            }}
                            id={fields?.parentId || ''}
                            selectedById={''}
                            fullWidth={true}
                            size="big"
                            selectNone={true}
                            queryParams={{ excludeIds: group.id }}
                        />
                    </FormGroup>
                    <FormGroup>
                        <FormLabel sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {t('profile.settings.generalSettings.timezone')}
                            {inheritTimezone && (
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <WarningAmberIcon fontSize="small" color="warning" />
                                    <Typography sx={{ color: 'warning.main' }} variant="body2">
                                        {t('group.groupForm.inheritedTimezoneMessage', {
                                            timezone: inheritTimezone,
                                        })}
                                    </Typography>
                                </Stack>
                            )}
                        </FormLabel>
                        <RoundedSelect
                            value={fields?.timezone}
                            fullWidth={true}
                            size="big"
                            onChange={(e) => {
                                handleFieldChange({
                                    target: {
                                        name: 'timezone',
                                        value: e.target.value,
                                    },
                                });
                            }}
                        >
                            <MenuItem value={''}>
                                <b>{t('common.none')}</b>
                            </MenuItem>
                            {moment.tz.names().map((tz) => {
                                return (
                                    <MenuItem value={tz} name={tz} key={tz}>
                                        {tz}
                                    </MenuItem>
                                );
                            })}
                        </RoundedSelect>
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>{t('event.eventFormSettings.description')}</FormLabel>
                        <FormControl variant="outlined" size="small">
                            <CustomTextFieldDescription
                                name="description"
                                value={fields.description}
                                onChange={handleFieldChange}
                                variant="outlined"
                                size="small"
                                multiline
                                rows={3}
                            />
                        </FormControl>
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>{t('unit.externalId')}</FormLabel>
                        <FormControl variant="outlined" size="small">
                            <CustomTextValidator
                                name="externalId"
                                fullWidth={true}
                                value={fields.externalId}
                                onChange={handleFieldChange}
                                variant="outlined"
                                size="small"
                            />
                        </FormControl>
                    </FormGroup>

                    <RenderFormFields formConfig={formConfig} fields={fields} handleFieldChange={handleFieldChange} />
                </Stack>

                <Stack direction="row" spacing={1} justifyContent="flex-end" className={flexBox.paddingTop2}>
                    {isProfile ? null : (
                        <ActionButton disabled={savePending} onClick={cancel} color="default" fitWidth>
                            {t('multimedia.multimediaForm.cancel')}
                        </ActionButton>
                    )}

                    {!isProfile || (isProfile && groupFormDirty) ? (
                        <ActionButton
                            disabled={savePending}
                            color="primary"
                            type="submit"
                            fitWidth
                            spinner={savePending}
                        >
                            {t('game.gameActions.save')}
                        </ActionButton>
                    ) : null}
                </Stack>
            </ValidatorForm>
        </LayoutForm>
    );
};

export default GroupForm;
